import React, { useState, useEffect, useRef } from 'react';
import { useAuth, API_URL, Currency, DateBR } from '../contexts/AuthContext';
import { Search, ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote, Barcode, Print, WhatsApp } from 'lucide-react';

export default function Vendas() {
  const { token, user } = useAuth();
  const [products, setProducts] = useState<any[]>([]);
  const [cart, setCart] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('money');
  const [amountReceived, setAmountReceived] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [session, setSession] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'ok'|'err'>('ok');
  const searchRef = useRef<HTMLInputElement>(null);

  const subtotal = cart.reduce((a, c) => a + c.unitPrice * c.qty, 0);
  const total = subtotal;
  const change = amountReceived ? Math.max(0, parseFloat(amountReceived) - total) : 0;

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/products?low_stock=true`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setProducts(d)).catch(() => {});
    fetch(`${API_URL}/cash/session/current`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null).then(s => setSession(s)).catch(() => {});
  }, [token]);

  const addProduct = (p: any) => {
    setCart(prev => {
      const ex = prev.find(c => c.product_id === p.id);
      if (ex) return prev.map(c => c.product_id === p.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { product_id: p.id, product_name: p.name, unitPrice: p.sale_price, qty: 1 }];
    });
    setSearch('');
    if (searchRef.current) searchRef.current.focus();
  };

  const removeItem = (id: string) => setCart(prev => prev.filter(c => c.product_id !== id));
  const changeQty = (id: string, delta: number) => setCart(prev => prev.map(c => c.product_id === id ? { ...c, qty: Math.max(0, c.qty + delta) } : c).filter(c => c.qty > 0));

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!search.trim()) return;
    try {
      const r = await fetch(`${API_URL}/products/barcode/${encodeURIComponent(search)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const p = await r.json();
        addProduct(p);
      } else {
        const r2 = await fetch(`${API_URL}/products?search=${encodeURIComponent(search)}`, { headers: { Authorization: `Bearer ${token}` } });
        const ps = await r2.json();
        if (ps.length) addProduct(ps[0]);
        else setMsg('Produto não encontrado'), setMsgType('err');
      }
    } catch { setMsg('Erro ao buscar produto'), setMsgType('err'); }
  };

  const checkout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session || session.status !== 'open') { setMsg('Caixa não está aberto'), setMsgType('err'); return; }
    if (!cart.length) { setMsg('Carrinho vazio'), setMsgType('err'); return; }
    try {
      const r = await fetch(`${API_URL}/sales/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ items: cart.map(c => ({ product_id: c.product_id, quantity: c.qty, unit_price: c.unitPrice })), payment_method, cash_session_id: session.id, customer_id, amount_received: amountReceived || 0 }),
      });
      const json = await r.json();
      if (!r.ok) { setMsg(json.error || 'Erro na venda'), setMsgType('err'); return; }
      setMsg(`Venda ${json.sale.sale_number} confirmada · Total ${Currency(json.total)} · Troco ${Currency(json.change)}`), setMsgType('ok');
      setCart([]); setAmountReceived(''); setCustomerId(null);
    } catch { setMsg('Erro ao registrar venda'), setMsgType('err'); }
  };

  const methods: { key: string; label: string; icon: React.ElementType }[] = [
    { key: 'money', label: 'Dinheiro', icon: Banknote },
    { key: 'pix', label: 'Pix', icon: CreditCard },
    { key: 'card', label: 'Cartão', icon: CreditCard },
    { key: 'credit', label: 'Fiado', icon: Banknote },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">PDV — Venda Rápida</h1>
        <p className="text-gray-600">Ponto de venda para celular e computador</p>
      </div>
      {msg && <div className={`rounded-lg p-3 text-sm ${msgType === 'ok' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>{msg}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Buscar produto</h2>
            <form onSubmit={handleSearch} className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input ref={searchRef} className="input pl-9" placeholder="Nome ou código de barras" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
              </div>
              <button className="btn-primary w-full">Adicionar ao carrinho</button>
            </form>
            <h2 className="text-sm font-semibold text-gray-700 mt-6 mb-3">Produtos disponíveis</h2>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {products.map(p => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm border border-gray-100">
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.brand} · {p.packaging} · est: {p.stock_quantity}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{Currency(p.sale_price)}</div>
                    <button onClick={() => addProduct(p)} className="text-xs btn-primary mt-1">+</button>
                  </div>
                </div>
              ))}
              {products.length === 0 && <p className="text-xs text-gray-500 text-center">Sem produtos</p>}
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Carrinho</h2>
            <div className="max-h-80 overflow-y-auto space-y-2 mb-4">
              {cart.map(c => (
                <div key={c.product_id} className="flex items-center justify-between px-3 py-2 bg-white border rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{c.product_name}</div>
                    <div className="text-xs text-gray-500">{Currency(c.unitPrice)} un</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => changeQty(c.product_id, -1)} className="w-7 h-7 rounded border border-gray-300 text-xs">−</button>
                    <span className="w-6 text-center font-medium">{c.qty}</span>
                    <button onClick={() => changeQty(c.product_id, 1)} className="w-7 h-7 rounded border border-gray-300 text-xs">+</button>
                    <button onClick={() => removeItem(c.product_id)} className="text-red-600 text-xs"><Trash2 size={16} /></button>
                  </div>
                  <div className="text-right font-semibold text-sm">{Currency(c.unitPrice * c.qty)}</div>
                </div>
              ))}
              {cart.length === 0 && <div className="text-center text-gray-500 text-sm py-4">Carrinho vazio</div>}
            </div>
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>{Currency(subtotal)}</span></div>
              <div className="flex justify-between text-sm font-bold"><span>Total</span><span>{Currency(total)}</span></div>
            </div>
            <div className="space-y-3 pt-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Forma de pagamento</label>
                <div className="grid grid-cols-2 gap-2">
                  {methods.map(m => {
                    const Icon = m.icon;
                    return (
                      <button key={m.key} onClick={() => setPaymentMethod(m.key)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${paymentMethod === m.key ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                        <Icon size={16} /> {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {(paymentMethod === 'money' || paymentMethod === 'credit') && (
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Valor recebido</label>
                  <input className="input" type="number" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} placeholder="0,00" />
                </div>
              )}
              {paymentMethod === 'credit' && (
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Cliente (fiado)</label>
                  <input className="input" type="text" placeholder="Nome do cliente" />
                </div>
              )}
              <div className="text-sm">
                <span className="text-gray-500">Troco: </span><span className="font-bold">{Currency(change)}</span>
              </div>
              <button onClick={checkout} className="btn-success w-full py-3 text-lg font-bold">Finalizar Venda</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
